import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  styled,
  TextField,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";

import { useForm, Controller, SubmitHandler } from "react-hook-form";

import { z } from "zod";

import { zodResolver } from "@hookform/resolvers/zod";

// API & Data Hooks
import { useAddSusunanKeluarga } from "../../../../api/effects/mutations/SusunanKeluarga/addSusunanKeluarga";
import { useEditSusunanKeluarga } from "../../../../api/effects/mutations/SusunanKeluarga/editSusunanKeluarga";
import { useFetchSusunanKeluarga } from "../../../../api/effects/mutations/SusunanKeluarga/useFetchSusunanKeluarga";
import useFetchAllPendidikanSelection from "../../../../api/effects/queries/RiwayatPendidikan/useFetchAllPendidikanSelection";
import useFetchHubunganKeluargaSelection from "../../../../api/effects/queries/SusunanKeluarga/useFetchHubunganKeluargaSelection";
import { PendidikanResponse, PendidikanSelectionResponse } from "../../../../models/Pendidikan";

// Models & Utils
import { HubunganKeluargaSelectionResponse } from "../../../../models/SusunanKeluarga";
import { formatDate, toIsoStringDate } from "../../../../utils/dateConvert";

// Custom Form Components
import InputDateForm from "../../../../components/InputDateForm";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";
import InputSelectForm from "../../../../components/InputSelectForm";
import InputCheckboxForm from "../../../../components/InputCheckboxForm";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(() => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

const InputWrapperGrid = styled(Grid)(() => ({
  padding: "1%",
}));

const TitleGrid = styled(Grid)(() => ({
  paddingLeft: "10%",
}));

const FullTextField = styled(TextField)(() => ({
  width: "80%",
}));

type FormValues = {
  nama: string;
  hubunganKeluarga: string;
  jenisKelamin: string;
  tempatLahir: string;
  tanggalLahir?: string;
  pendidikan: string;
  perusahaan: string;
  jabatan: string;
  almarhum: boolean;
  isApplicant: boolean;
  noTanggalLahir: boolean;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  type: string;
  ID?: number;
  isApplicantAlreadyExists?: boolean;
};

type SelectionOption = {
  value: string;
  text: string;
};

const jenisKelaminSelection = [
  {
    value: "Laki-Laki",
    text: "Laki-Laki",
  },
  {
    value: "Perempuan",
    text: "Perempuan",
  },
];

export default function SusunanKeluargaModal({
  ID = -1,
  open,
  type,
  handleClose,
  isApplicantAlreadyExists = false,
}: Props) {
  const [hubunganKeluargaSelectionData, setHubunganKeluargaSelectionData] =
    useState<HubunganKeluargaSelectionResponse[]>([]);

  const { data: PendidikanData } = useFetchAllPendidikanSelection();
  const { data: HubunganKeluargaData } = useFetchHubunganKeluargaSelection();
  const [jenisKelaminSelectionData, setJenisKelaminSelectionData] =
    useState<SelectionOption[]>(jenisKelaminSelection);
  const [pendidikanSelectionData, setPendidikanSelectionData] =
    useState<PendidikanSelectionResponse[]>([]);
  const parseDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  };
  const schema = z
    .object({
      nama: z.string().min(1, "Nama field is required"),
      hubunganKeluarga: z.string().min(1, "Hubungan Keluarga field is required"),
      jenisKelamin: z.string().min(1, "Jenis Kelamin field is required"),
      tempatLahir: z.string().min(1, "Tempat Lahir field is required"),
      tanggalLahir: z.string().optional()
        .refine(val => {
          if (!val) return true;
          const date = parseDate(val);
          return !isNaN(date.getTime());
        }, { message: "Invalid date format" })
        .refine(val => {
          if (!val) return true;
          const date = new Date(val);
          const minDate = new Date(1900, 0, 1);
          const maxDate = new Date();
          return date >= minDate && date <= maxDate;
        }, { message: "Date must be between January 1, 1900 and today" }),
      pendidikan: z.string().min(1, "Please choose Pendidikan Terakhir"),
      perusahaan: z.string().min(1, "this field can't be empty"),
      jabatan: z.string().min(1, "this field can't be empty"),
      almarhum: z.boolean(),
      isApplicant: z.boolean(),
      noTanggalLahir: z.boolean(),
    })
    .refine((data) => (data.noTanggalLahir ? true : !!data.tanggalLahir), {
      message: "Tanggal Lahir is required",
      path: ["tanggalLahir"],
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      nama: "",
      hubunganKeluarga: "",
      jenisKelamin: "",
      tempatLahir: "",
      tanggalLahir: "",
      pendidikan: "",
      perusahaan: "",
      jabatan: "",
      almarhum: false,
      isApplicant: false,
      noTanggalLahir: false,
    },
  });

  const noTanggalLahir = watch("noTanggalLahir");
  const hubunganKeluarga = watch("hubunganKeluarga");
  const isApplicantVisible = type === "OT" && Number(hubunganKeluarga) > 2;

  useEffect(() => {
    if (noTanggalLahir) {
      setValue("tanggalLahir", "", { shouldValidate: true });
    }
  }, [noTanggalLahir, setValue]);

  useEffect(() => {
    if (hubunganKeluarga === "1" || hubunganKeluarga === "2") {
      setValue("isApplicant", false);
    }
  }, [hubunganKeluarga, setValue]);

  const { mutateAsync: GetSusunanKeluargaData } = useFetchSusunanKeluarga({
    onSuccess(data) {
      setValue("nama", data.NAMA ?? "");
      setValue("hubunganKeluarga", String(data.HUBUNGAN_KELUARGA_ID ?? ""));
      setValue("jenisKelamin", data.JENIS_KELAMIN ?? "");
      setValue("tempatLahir", data.TEMPAT_LAHIR ?? "");
      setValue("tanggalLahir", formatDate(data.TANGGAL_LAHIR));
      setValue("pendidikan", String(data.PENDIDIKAN_ID ?? ""));
      setValue("perusahaan", data.PERUSAHAAN_TERAKHIR ?? "-");
      setValue("jabatan", data.JABATAN_TERAKHIR ?? "-");
      setValue("almarhum", !!data.ALMARHUM);
      setValue("isApplicant", !!data.IS_APPLICANT);
      setValue("noTanggalLahir", !data.TANGGAL_LAHIR);
    },
    onError: (error) =>
      console.error("Error fetching family member data:", error),
  });

  useEffect(() => {
    if (PendidikanData && HubunganKeluargaData) {
      const filteredHubungan =
        type === "OT"
          ? HubunganKeluargaData.filter((e) => Number(e.value) < 12)
          : HubunganKeluargaData.filter((e) => Number(e.value) > 11);
      setHubunganKeluargaSelectionData(filteredHubungan);
    }
  }, [PendidikanData, HubunganKeluargaData, type]);

  useEffect(() => {
    if (jenisKelaminSelection !== undefined) {
      setJenisKelaminSelectionData(jenisKelaminSelection);
    }
    setJenisKelaminSelectionData(jenisKelaminSelection);
    if (!open) {
      reset();
    }
  }, [jenisKelaminSelection, open, reset]);

  useEffect(() => {
    if (open && ID > 0) {
      GetSusunanKeluargaData(ID);
    } else if (!open) {
      reset();
    }
  }, [ID, open, GetSusunanKeluargaData, reset]);

  useEffect(() => {
    if (PendidikanData !== undefined) {
      setPendidikanSelectionData(PendidikanData);
    }
    if (!open) {
      reset();
    }
  }, [PendidikanData, open, reset]);

  const { mutateAsync: AddSusunanKeluarga, isLoading: isAdding } = useAddSusunanKeluarga();
  const { mutateAsync: EditSusunanKeluarga, isLoading: isEditing } = useEditSusunanKeluarga();

  const handleModalKeluargaClose = () => {
    reset();
    handleClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload = {
      HUBUNGAN_KELUARGA_ID: data.hubunganKeluarga,
      JENIS_KELAMIN: data.jenisKelamin,
      NAMA: data.nama,
      TEMPAT_LAHIR: data.tempatLahir,
      TANGGAL_LAHIR: data.noTanggalLahir
        ? null
        : toIsoStringDate(data.tanggalLahir!),
      PENDIDIKAN_ID: data.pendidikan,
      PERUSAHAAN_TERAKHIR: data.perusahaan,
      JABATAN_TERAKHIR: data.jabatan,
      ALMARHUM: data.almarhum,
      IS_APPLICANT: isApplicantVisible ? data.isApplicant : false,
    };

    try {
      if (ID > 0) {
        await EditSusunanKeluarga({ SUSUNAN_KELUARGA_ID: ID, ...payload });
      } else {
        await AddSusunanKeluarga(payload);
      }
      handleModalKeluargaClose();
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1
                  ? "Tambah Susunan Keluarga"
                  : "Ubah Susunan Keluarga"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleModalKeluargaClose}>Close</Button>
            </Grid>

            {/* Hubungan Keluarga - Select */}
            <InputSelectForm
              name="hubunganKeluarga"
              title="Hubungan Keluarga"
              control={control}
              options={hubunganKeluargaSelectionData}
              placeholder="-- Pilih Hubungan Keluarga --"
            />

            {/* Jenis Kelamin - Select */}
            <InputSelectForm
              name="jenisKelamin"
              title="Jenis Kelamin"
              control={control}
              options={jenisKelaminSelectionData}
              placeholder="-- Pilih Jenis Kelamin --"
            />

            {/* Nama - Text */}
            <InputTextFieldForm
              name="nama"
              control={control}
              label="Nama"
              error={errors.nama}
            />

            {/* isApplicant Checkbox */}
            {!isApplicantAlreadyExists &&
              hubunganKeluarga !== "1" && // Hide if Ayah
              hubunganKeluarga !== "2" && ( // Hide if Ibu (
                <Grid container justifyContent="center">
                  <Grid
                    container
                    justifyContent="flex-start"
                    sx={{ paddingLeft: "11%" }}
                  >
                    <Controller
                      name="isApplicant"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel

                          label={
                            <Typography component="span" color="black">
                              Diri Sendiri
                            </Typography>
                          }
                          control={
                            <Checkbox
                              {...field}
                              checked={!!field.value}
                              title="Pelamar Kerja"


                            />
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}

            {/* Tempat Lahir - Text */}
            <InputTextFieldForm
              name="tempatLahir"
              control={control}
              label="Tempat Lahir"
              error={errors.tempatLahir}
            />

            {!noTanggalLahir && (
              <InputDateForm
                name="tanggalLahir"
                control={control}
                label="Tanggal Lahir"
                error={errors.tanggalLahir}
                disabled={false}
              />
            )}

            {/* noTanggalLahir Checkbox */}
            <Grid item xs={11}>
              <InputCheckboxForm
                name="noTanggalLahir"
                control={control}
                label="No Tanggal Lahir"
                checkboxLabel="Tidak Tahu Tanggal Lahir"
                targetFieldName="tanggalLahir"
                setValue={setValue}
              />
            </Grid>


            {/* Pendidikan - Select */}
            <InputSelectForm
              name="pendidikan"
              title="Pendidikan"
              control={control}
              options={pendidikanSelectionData}
              placeholder="-- Pilih Pendidikan --"
            />

            {/* Perusahaan Terakhir - Text */}
            <InputTextFieldForm
              name="perusahaan"
              control={control}
              label="Perusahaan Terakhir"
              error={errors.perusahaan}
            />

            {/* Jabatan Terakhir - Text */}
            <InputTextFieldForm
              name="jabatan"
              control={control}
              label="Jabatan Terakhir"
              error={errors.jabatan}
            />

            {/* Almarhum - Radio */}
            <Grid container justifyContent="center">
              <InputWrapperGrid container justifyContent="center">
                <FormControl sx={{ width: "80%" }}>
                  <FormLabel
                    id="almarhum-radio-buttons-group-label"
                    sx={{
                      color: "black",
                      textAlign: "left",
                      fontSize: "1.25rem",
                      fontWeight: 500,
                    }}
                  >
                    Almarhum
                  </FormLabel>
                  <Controller
                    name="almarhum"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        aria-labelledby="almarhum-radio-buttons-group-label"
                        value={field.value ? "true" : "false"}
                        onChange={(e) =>
                          field.onChange(e.target.value === "true")
                        }
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio />}
                          label={
                            <Typography component="span" color="black">
                              Ya
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio />}
                          label={
                            <Typography component="span" color="black">
                              Tidak
                            </Typography>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </InputWrapperGrid>
            </Grid>

            <Grid container justifyContent="center" item xs={12} sx={{ mt: 2 }}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop>
  );
}
